import * as React from "react";
import { cn } from "@/lib/utils";
import { tenant } from "@/lib/tenant";
import { useTheme } from "@/providers/ThemeProvider";

type Size = "sm" | "md" | "lg";

interface TenantBrandProps {
	size?: Size;
	className?: string;
	/** When true, force-show the brand text even if the logo includes it. */
	forceText?: boolean;
	/** When true, force-hide the brand text even if the logo doesn't include it. */
	hideText?: boolean;
}

/**
 * Renders the tenant logo (and optional brand name).
 * Logo width adapts based on whether the SVG already includes the brand text:
 *   - logoIncludesText=true → wider logo, no extra text
 *   - logoIncludesText=false → square logo + text next to it
 *
 * When the tenant config provides `logoDark`, that variant is used while the
 * dark theme is active (e.g. wordmarks with dark text that wouldn't be
 * readable on a dark canvas).
 */
export function TenantBrand({
	size = "md",
	className,
	forceText,
	hideText,
}: TenantBrandProps) {
	const { resolvedTheme } = useTheme();
	const isDark = resolvedTheme === "dark";

	const includesText = !!tenant.logoIncludesText;
	const showText = forceText ? true : hideText ? false : !includesText;

	const logoSrc = isDark && tenant.logoDark ? tenant.logoDark : tenant.logo;

	// Logo dimensions per size. Bumped ~30% from the previous baseline so the
	// horizontal wordmark stays readable in the navbar.
	const dims = {
		sm: includesText ? "h-5 w-auto" : "w-9 h-9",
		md: includesText ? "h-7 w-auto" : "w-10 h-10",
		lg: includesText ? "h-8 w-auto" : "w-12 h-12",
	}[size];

	const textSize = {
		sm: "text-sm",
		md: "text-base",
		lg: "text-lg",
	}[size];

	return (
		<span className={cn("flex items-center gap-2 shrink-0", className)}>
			<img src={logoSrc} alt={tenant.brand} className={dims} draggable={false} />
			{showText && (
				<span className={cn("font-bold tracking-tight", textSize)}>
					{tenant.brand}
				</span>
			)}
		</span>
	);
}
